import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, Award, Sparkles, ArrowRight, Share2, ClipboardCheck, Copy, ThumbsUp, Send, Check, Clock, Building2, X, RotateCcw } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { getUser, listAssessments, getUsers, createEndorsementRequest, listEndorsementRequests } from "@/lib/api";
import { Header, SkillTag, CredentialBadge, StatCard, categoryIcon } from "@/components/shared";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";

export default function WorkerDashboard() {
  const { currentUser } = useApp();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [employers, setEmployers] = useState([]);
  const [requests, setRequests] = useState([]);
  const [askSkill, setAskSkill] = useState(null);

  const load = () => {
    getUser(currentUser.id).then(setData);
    listAssessments(currentUser.id).then(setSessions);
    listEndorsementRequests({ worker_id: currentUser.id }).then(setRequests);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [currentUser.id]);
  useEffect(() => { getUsers("employer").then(setEmployers); }, []);

  const shareResume = () => {
    const url = `${window.location.origin}/resume/${currentUser.id}`;
    navigator.clipboard?.writeText(url);
    toast.success("Public resume link copied!", { description: url });
  };

  const requestFor = (skillId) =>
    requests.filter((r) => r.skill_id === skillId);

  const requestEndorsement = async (employerId) => {
    try {
      const res = await createEndorsementRequest(currentUser.id, employerId, askSkill.id);
      if (res.created) {
        toast.success("Endorsement request sent");
      } else {
        toast.info("Already requested from this employer");
      }
      const updated = await listEndorsementRequests({ worker_id: currentUser.id });
      setRequests(updated);
      setAskSkill(null);
    } catch {
      toast.error("Could not send request");
    }
  };

  if (!data) {
    return (
      <div className="min-h-screen">
        <Header title="Worker" />
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 space-y-4">
          <Skeleton className="h-40 rounded-lg" />
          <Skeleton className="h-64 rounded-lg" />
        </div>
      </div>
    );
  }

  const verified = data.skills.filter((s) => s.verified_status === "verified");

  return (
    <div className="min-h-screen grain">
      <Header
        title="Worker"
        right={
          <Button data-testid="header-share-btn" variant="outline" size="sm" onClick={shareResume}>
            <Share2 className="w-4 h-4 sm:mr-1.5" />
            <span className="hidden sm:inline">Share Resume</span>
          </Button>
        }
      />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        {/* Greeting */}
        <div className="flex items-center gap-4">
          <Avatar className="w-16 h-16 border border-border">
            <AvatarImage src={data.avatar_url} className="object-cover" />
            <AvatarFallback>{data.name[0]}</AvatarFallback>
          </Avatar>
          <div>
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-muted-foreground">Welcome back</p>
            <h1 className="font-heading text-2xl sm:text-3xl font-black tracking-tight">{data.name}</h1>
            <p className="text-sm text-muted-foreground">{data.headline} · {data.location}</p>
          </div>
        </div>

        {/* Big CTA */}
        <motion.button
          data-testid="ai-assessment-btn"
          whileTap={{ scale: 0.98 }}
          onClick={() => navigate("/worker/assessment")}
          className="mt-6 w-full rounded-lg bg-primary text-primary-foreground p-5 sm:p-6 flex items-center justify-between gap-4 shadow-sm text-left border border-primary/40 hover:brightness-110 transition-all"
        >
          <div>
            <div className="inline-flex items-center gap-2 rounded-md bg-white/15 px-2 py-0.5 text-[10px] font-semibold tracking-[0.12em] uppercase">
              <Sparkles className="w-3 h-3" /> AI-Powered
            </div>
            <div className="mt-2 font-heading text-xl sm:text-2xl font-extrabold tracking-tight">Take an AI skill assessment</div>
            <div className="mt-1 text-sm text-primary-foreground/85 max-w-md">
              Answer a few practical questions. Our AI turns your experience into verified credentials.
            </div>
          </div>
          <div className="w-10 h-10 rounded-md bg-white/20 flex items-center justify-center shrink-0">
            <ArrowRight className="w-5 h-5" />
          </div>
        </motion.button>

        {/* Stats */}
        <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 gap-3">
          <StatCard testid="stat-verified" label="Verified Skills" value={verified.length} icon={<ShieldCheck className="w-5 h-5" />} />
          <StatCard testid="stat-badges" label="Digital Badges" value={data.badges.length} icon={<Award className="w-5 h-5" />} />
          <StatCard testid="stat-assessments" label="Assessments" value={sessions.filter((s) => s.status === "completed").length} icon={<ClipboardCheck className="w-5 h-5" />} />
        </div>

        {/* Badges */}
        <section className="mt-8">
          <h2 className="font-heading text-xl font-bold tracking-tight mb-4">Digital Credential Badges</h2>
          {data.badges.length === 0 ? (
            <p className="text-sm text-muted-foreground">No badges yet — take an assessment to earn your first.</p>
          ) : (
            <div className="flex flex-wrap gap-5 rounded-lg border border-border bg-card p-6">
              {data.badges.map((b, i) => <CredentialBadge key={b.id} badge={b} index={i} />)}
            </div>
          )}
        </section>

        {/* Skills with endorsement requests */}
        <section className="mt-8">
          <div className="flex items-baseline justify-between mb-4">
            <h2 className="font-heading text-xl font-bold tracking-tight">Verified Skills</h2>
            <p className="text-xs text-muted-foreground">Request endorsements from verified employers</p>
          </div>
          <div className="space-y-2.5">
            {verified.map((s) => {
              const reqs = requestFor(s.id);
              const pending = reqs.find((r) => r.status === "pending");
              const fulfilledCount = reqs.filter((r) => r.status === "fulfilled").length;
              return (
                <div
                  key={s.id}
                  data-testid={`worker-skill-row-${s.id}`}
                  className="rounded-lg border border-border bg-card p-3 flex flex-col sm:flex-row sm:items-center gap-3"
                >
                  <div className="flex-1"><SkillTag skill={s} testid={`skill-${s.id}`} /></div>
                  <div className="flex items-center gap-2 flex-wrap">
                    {pending && (
                      <span data-testid={`pending-badge-${s.id}`} className="inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-800 dark:bg-amber-500/20 dark:text-amber-200 px-2.5 py-1 text-xs font-bold">
                        <Clock className="w-3 h-3" /> {reqs.filter((r) => r.status === "pending").length} pending
                      </span>
                    )}
                    {fulfilledCount > 0 && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 text-primary px-2.5 py-1 text-xs font-bold">
                        <Check className="w-3 h-3" /> {fulfilledCount} endorsed
                      </span>
                    )}
                    <Button
                      data-testid={`request-endorsement-btn-${s.id}`}
                      variant="outline"
                      size="sm"
                      onClick={() => setAskSkill(s)}
                    >
                      <Send className="w-3.5 h-3.5 mr-1.5" /> Request endorsement
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* Resume link */}
        <section className="mt-8 rounded-lg border border-border bg-card p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h3 className="font-heading font-bold text-lg">Your Public Resume</h3>
            <p className="text-sm text-muted-foreground font-mono break-all">{window.location.origin}/resume/{data.id}</p>
          </div>
          <div className="flex gap-2">
            <Button data-testid="view-resume-btn" variant="outline" onClick={() => navigate(`/resume/${data.id}`)}>View</Button>
            <Button data-testid="copy-resume-btn" onClick={shareResume}><Copy className="w-4 h-4 mr-1.5" />Copy Link</Button>
          </div>
        </section>

        {/* Assessment history */}
        {sessions.filter((s) => s.status === "completed").length > 0 && (
          <section className="mt-8">
            <h2 className="font-heading text-xl font-bold tracking-tight mb-4">Assessment History</h2>
            <div className="space-y-3">
              {sessions.filter((s) => s.status === "completed").map((s) => (
                <div key={s.id} data-testid={`assessment-history-${s.id}`} className="rounded-lg border border-border bg-card p-5 flex flex-col sm:flex-row sm:items-start gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 text-sm font-semibold text-primary">
                      {categoryIcon(s.category)} {s.category}
                    </div>
                    <p className="mt-2 text-sm text-foreground/80">{s.ai_evaluation_summary}</p>
                  </div>
                  <Button
                    data-testid={`retake-${s.category.replace(/\s+/g, "-").toLowerCase()}`}
                    variant="outline"
                    size="sm"
                    onClick={() => navigate(`/worker/assessment?retake=${encodeURIComponent(s.category)}`)}
                    className="shrink-0"
                  >
                    <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Retake to level up
                  </Button>
                </div>
              ))}
            </div>
          </section>
        )}
      </div>

      <Dialog open={!!askSkill} onOpenChange={(o) => !o && setAskSkill(null)}>
        <DialogContent className="max-w-md" data-testid="ask-endorsement-modal">
          <DialogTitle className="font-heading text-xl font-bold flex items-center gap-2">
            <ThumbsUp className="w-5 h-5 text-primary" /> Request endorsement
          </DialogTitle>
          <DialogDescription>
            {askSkill && <>Ask a verified employer to endorse your <strong className="text-foreground">{askSkill.skill_name}</strong> skill.</>}
          </DialogDescription>
          <div className="mt-2 max-h-80 overflow-y-auto space-y-2">
            {employers.map((e) => {
              const reqs = askSkill ? requests.filter((r) => r.skill_id === askSkill.id && r.employer_id === e.id) : [];
              const pending = reqs.find((r) => r.status === "pending");
              const fulfilled = reqs.find((r) => r.status === "fulfilled");
              return (
                <button
                  key={e.id}
                  data-testid={`ask-employer-${e.id}`}
                  onClick={() => !pending && !fulfilled && requestEndorsement(e.id)}
                  disabled={!!pending || !!fulfilled}
                  className="w-full text-left rounded-xl border border-border p-3 flex items-center gap-3 hover:border-primary transition-colors disabled:opacity-60 disabled:hover:border-border"
                >
                  <Avatar className="w-10 h-10 border border-border">
                    <AvatarImage src={e.avatar_url} className="object-cover" />
                    <AvatarFallback>{e.name[0]}</AvatarFallback>
                  </Avatar>
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold truncate">{e.name}</div>
                    <div className="text-xs text-muted-foreground inline-flex items-center gap-1 truncate">
                      <Building2 className="w-3 h-3" />{e.company}
                    </div>
                  </div>
                  {fulfilled ? (
                    <span className="text-xs font-bold text-primary inline-flex items-center gap-1"><Check className="w-3.5 h-3.5" /> Endorsed</span>
                  ) : pending ? (
                    <span className="text-xs font-bold text-amber-700 dark:text-amber-300 inline-flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> Pending</span>
                  ) : (
                    <Send className="w-4 h-4 text-primary" />
                  )}
                </button>
              );
            })}
            {employers.length === 0 && <p className="text-sm text-muted-foreground text-center py-6">No verified employers available.</p>}
          </div>
          <Button variant="ghost" size="sm" onClick={() => setAskSkill(null)} className="mt-1">
            <X className="w-4 h-4 mr-1" /> Cancel
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
